/**
 * @description Markdown 渲染组件
 * @author Cursor
 * @date 2026-02-03
 */
import React, { useEffect, useState } from 'react';
import { Spin } from 'antd';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import 'property-information';
import styles from './style.module.less';

/**
 * MarkDownRender 组件属性类型 - write by cursor
 */
interface IMarkDownRenderProps {
  url: string | null;
  loading?: boolean;
}

/**
 * Markdown 内容响应类型 - write by cursor
 */
type MarkdownResponse = string | { data?: string } | { data: string };

/**
 * Markdown 渲染组件 - write by cursor
 */
const MarkDownRender: React.FC<IMarkDownRenderProps> = (props) => {
  const { loading: externalLoading } = props;
  const [source, setSource] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  /**
   * 加载 Markdown 内容 - write by cursor
   */
  useEffect(() => {
    if (props.url) {
      const fetchMarkdown = async () => {
        try {
          setLoading(true);
          // 使用 axios.get 直接调用接口，参考 token-manager.ts 的写法 - write by cursor
          const response = await axios.get<MarkdownResponse>(props.url!, {
            timeout: 30000,
            headers: {
              'Content-Type': 'application/json',
            },
          });
          // 如果响应是字符串，直接使用；如果是对象，取 data 字段 - write by cursor
          let content: string | null = null;
          if (typeof response.data === 'string') {
            content = response.data;
          } else if (response.data && typeof response.data === 'object') {
            const dataObj = response.data as { data?: string };
            content = dataObj.data || null;
          }
          setSource(content);
        } catch (err) {
          console.error('Failed to fetch markdown content:', err);
          setSource(null);
        } finally {
          setLoading(false);
        }
      };

      fetchMarkdown();
    }
  }, [props.url]);

  // 如果外部传入 loading 状态，优先显示外部 loading - write by cursor
  if (externalLoading || loading) {
    return (
      <div className={styles.mdLoadingContainer}>
        <Spin spinning={externalLoading || loading} />
      </div>
    );
  }

  /**
   * 自定义组件配置，确保表格正确渲染 - write by cursor
   */
  const components = {
    // 表格相关组件配置，确保表格元素正确渲染 - write by cursor
    table: ({ node, className, ...props }: any) => {
      return (
        <div style={{ overflowX: 'auto', marginBottom: '16px' }}>
          <table
            className={className}
            {...props}
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              border: '1px solid #ddd',
              display: 'table',
            }}
          />
        </div>
      );
    },
    thead: ({ node, className, ...props }: any) => (
      <thead className={className} {...props} style={{ display: 'table-header-group' }} />
    ),
    tbody: ({ node, className, ...props }: any) => (
      <tbody className={className} {...props} style={{ display: 'table-row-group' }} />
    ),
    tr: ({ node, className, ...props }: any) => (
      <tr className={className} {...props} style={{ display: 'table-row' }} />
    ),
    th: ({ node, className, ...props }: any) => (
      <th
        className={className}
        {...props}
        style={{
          display: 'table-cell',
          padding: '10px 12px',
          border: '1px solid #ddd',
          textAlign: 'left',
          fontWeight: 600,
          backgroundColor: '#fafafa',
        }}
      />
    ),
    td: ({ node, className, ...props }: any) => (
      <td
        className={className}
        {...props}
        style={{
          display: 'table-cell',
          padding: '10px 12px',
          border: '1px solid #ddd',
          textAlign: 'left',
        }}
      />
    ),
  };

  return (
    <div className={styles.mdContainer}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={components}
        skipHtml={false}
      >
        {source || ''}
      </ReactMarkdown>
    </div>
  );
};

export default MarkDownRender;
